import { Component } from 'preact';
import { controllerMode } from '../../utils/input/controllerMode';
import { createGamepadPoll } from '../../utils/input/gamepadPoll';
import { createVirtualKeyboardController } from '../../utils/input/virtualKeyboardController';
import { GAMEPAD_BUTTON } from '../../utils/game/keybindings';

// Global on-screen keyboard. Renders only when controllerMode is
// 'text' (the mode observer flips to that whenever a text <input> /
// <textarea> / contenteditable region gains focus). Subscribes to a
// private gamepadPoll so it can drive its cursor without disturbing
// the spatial focus bridge (which stood down the moment mode left
// 'menu').

function injectCharacter(el, char) {
  if (!el) return;
  if (el.isContentEditable) {
    try { document.execCommand('insertText', false, char); } catch (_) {}
    return;
  }
  const proto = el.tagName === 'TEXTAREA' ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  if (!setter) return;
  const start = el.selectionStart ?? el.value.length;
  const end = el.selectionEnd ?? start;
  const nextValue = `${el.value.slice(0, start)}${char}${el.value.slice(end)}`;
  setter.call(el, nextValue);
  const nextCaret = start + char.length;
  try { el.setSelectionRange(nextCaret, nextCaret); } catch (_) {}
  el.dispatchEvent(new InputEvent('input', { bubbles: true, data: char, inputType: 'insertText' }));
}

function injectBackspace(el) {
  if (!el) return;
  if (el.isContentEditable) {
    try { document.execCommand('delete'); } catch (_) {}
    return;
  }
  const proto = el.tagName === 'TEXTAREA' ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  if (!setter) return;
  let start = el.selectionStart ?? el.value.length;
  let end = el.selectionEnd ?? start;
  if (start === end) {
    if (start === 0) return;
    start -= 1;
  }
  const nextValue = `${el.value.slice(0, start)}${el.value.slice(end)}`;
  setter.call(el, nextValue);
  try { el.setSelectionRange(start, start); } catch (_) {}
  el.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'deleteContentBackward' }));
}

function injectEnter(el) {
  if (!el) return;
  const keyEvent = new KeyboardEvent('keydown', {
    bubbles: true, cancelable: true, key: 'Enter', code: 'Enter', keyCode: 13, which: 13,
  });
  el.dispatchEvent(keyEvent);
  // For textareas, also inject a newline character if the handler
  // didn't preventDefault (preventDefault isn't observable on
  // synthetic events reliably, so only textareas get the newline.
  // single-line inputs generally submit on Enter and discard it).
  if (el.tagName === 'TEXTAREA') injectCharacter(el, '\n');
}

export default class VirtualKeyboard extends Component {
  // `summoned` is the ONLY signal for visibility. It toggles on a
  // Start+X chord. Never automatically on text focus. So mouse/
  // keyboard users never see the OSK even when typing.
  state = { summoned: false, snapshot: null };
  startHeld = false;

  componentDidMount() {
    this.ctrl = createVirtualKeyboardController();
    // Always-on gamepad poll so we can detect the Start+X chord from
    // anywhere. Cheap: idle at zero cost when no pad is present.
    this.poll = createGamepadPoll({ padIndex: 0 });
    this.offs = [
      this.poll.on('button-down', (e) => this.handleButtonDown(e.detail.index)),
      this.poll.on('button-up',   (e) => this.handleButtonUp(e.detail.index)),
    ];
    this.poll.start();
  }

  componentWillUnmount() {
    if (this.offs) { for (const off of this.offs) off(); this.offs = null; }
    if (this.poll) { this.poll.stop(); this.poll = null; }
    // Defensive: if we were summoned on unmount, release the text-mode
    // hold so the focus bridge isn't stuck standing down.
    if (this.state.summoned && controllerMode.get() === 'text') {
      controllerMode.set('menu');
    }
  }

  toggleSummoned = () => {
    // Never summon during a match. GameBoard owns all input there.
    if (!this.state.summoned && controllerMode.get() === 'board') return;
    const next = !this.state.summoned;
    if (next) {
      controllerMode.set('text');
      this.setState({ summoned: true, snapshot: this.ctrl.snapshot() });
    } else {
      // If the page had hoisted us into 'text' mode (board → match),
      // restore to 'menu' on close. Leaves 'board' mode alone.
      if (controllerMode.get() === 'text') controllerMode.set('menu');
      this.setState({ summoned: false, snapshot: null });
    }
  };

  handleButtonDown = (index) => {
    // Chord detection. Checked before normal OSK key handling so
    // pressing X while Start is held doesn't type 'x' into the input.
    if (index === GAMEPAD_BUTTON.START) {
      this.startHeld = true;
      // Start alone behaves normally (see below: inject Enter) only
      // when the OSK is already summoned.
    }
    if (index === GAMEPAD_BUTTON.X && this.startHeld) {
      this.toggleSummoned();
      return;
    }

    if (!this.state.summoned) return;

    const ctrl = this.ctrl;
    if (!ctrl) return;
    switch (index) {
      case GAMEPAD_BUTTON.DPAD_UP:    ctrl.move('up'); break;
      case GAMEPAD_BUTTON.DPAD_DOWN:  ctrl.move('down'); break;
      case GAMEPAD_BUTTON.DPAD_LEFT:  ctrl.move('left'); break;
      case GAMEPAD_BUTTON.DPAD_RIGHT: ctrl.move('right'); break;
      case GAMEPAD_BUTTON.A: {
        const action = ctrl.pressCurrent();
        this.applyAction(action);
        break;
      }
      case GAMEPAD_BUTTON.B: {
        injectBackspace(document.activeElement);
        break;
      }
      case GAMEPAD_BUTTON.START: {
        injectEnter(document.activeElement);
        break;
      }
      case GAMEPAD_BUTTON.BACK: {
        this.toggleSummoned();
        return;
      }
      case GAMEPAD_BUTTON.LB:
      case GAMEPAD_BUTTON.RB: {
        ctrl.togglePage();
        break;
      }
      default: return;
    }
    this.setState({ snapshot: ctrl.snapshot() });
  };

  handleButtonUp = (index) => {
    if (index === GAMEPAD_BUTTON.START) this.startHeld = false;
  };

  applyAction = (action) => {
    if (!action) return;
    const el = document.activeElement;
    if (action.type === 'char') { injectCharacter(el, action.value); return; }
    if (action.type === 'modal') { /* state already mutated inside the controller */ return; }
    if (action.type === 'action') {
      if (action.value === 'space') injectCharacter(el, ' ');
      else if (action.value === 'backspace') injectBackspace(el);
      else if (action.value === 'enter') injectEnter(el);
      else if (action.value === 'close') {
        this.toggleSummoned();
      }
    }
  };

  renderKey = (key, r, c, snapshot) => {
    if (!key) return null;
    const label = this.keyLabel(key, snapshot);
    const isFocused = snapshot.row === r && snapshot.col === c;
    const widthUnits = key.width || 1;
    const baseCls = `px-2.5 py-1.5 rounded-md text-sm font-semibold transition-colors select-none border`;
    const style = {
      gridColumn: `span ${widthUnits}`,
      background: isFocused ? 'rgba(253,230,138,0.9)' : 'rgba(20,17,12,0.85)',
      color: isFocused ? '#1a1308' : '#e7e2dc',
      borderColor: isFocused ? 'rgba(253,230,138,1)' : 'rgba(180,140,60,0.3)',
      minWidth: '2.125cqw',
      textAlign: 'center',
    };
    return <div key={`${r}-${c}-${label}`} className={baseCls} style={style}>{label}</div>;
  };

  keyLabel = (key, snapshot) => {
    if (key.type === 'char') return snapshot.shift ? key.value.toUpperCase() : key.value;
    if (key.type === 'modal' && key.value === 'shift') {
      return snapshot.capsLock ? '⇪' : (snapshot.shift ? '⇧•' : '⇧');
    }
    if (key.type === 'modal' && key.value === 'page') return snapshot.page === 'abc' ? 'Sym' : 'ABC';
    if (key.type === 'action') {
      if (key.value === 'space') return 'Space';
      if (key.value === 'backspace') return '⌫';
      if (key.value === 'enter') return 'Enter';
      if (key.value === 'close') return 'Close';
    }
    return '?';
  };

  render() {
    const { summoned, snapshot } = this.state;
    if (!summoned || !snapshot) return null;
    const { layout } = snapshot;

    return (
      <div
        style={{
          position: 'fixed',
          left: '50%',
          bottom: '1.5cqw',
          transform: 'translateX(-50%)',
          zIndex: 9999,
          padding: '0.875cqw 1cqw',
          background: 'rgba(12,10,8,0.92)',
          border: '1px solid rgba(180,140,60,0.35)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.75)',
          pointerEvents: 'none',
          backdropFilter: 'blur(6px)',
          maxWidth: '95vw',
        }}
        aria-hidden="true"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375cqw' }}>
          {layout.map((rowKeys, r) => (
            <div
              key={`row-${r}`}
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${rowKeys.reduce((sum, k) => sum + (k.width || 1), 0)}, minmax(2.125cqw, 1fr))`,
                gap: '0.25cqw',
                justifyContent: 'center',
              }}
            >
              {rowKeys.map((key, c) => this.renderKey(key, r, c, snapshot))}
            </div>
          ))}
        </div>
        <div style={{ marginTop: '0.5cqw', textAlign: 'center', fontSize: '0.625cqw', color: 'rgba(200,195,188,0.55)' }}>
          A type · B backspace · Start enter · Back / Start+X close · LB/RB page · D-pad move
        </div>
      </div>
    );
  }
}
